import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Checkbox, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { DataGrid, GridActionsCellItem, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { frFR } from '@mui/x-data-grid/locales';
import { toast } from 'react-toastify';
import { Supplier, SupplierGroup, SupplierPayload } from '../../../../models/supplierAdministration';
import { supplierAdministrationApi } from '../../../../services/supplierAdministrationApi';
import { CustomPaper } from '../../styles';
import ConfirmDialog from './ConfirmDialog';
import SupplierDialog from './SupplierDialog';

type SuppliersTabProps = {
	canModify: boolean;
	canCreateAddress: boolean;
}

type StatusAction = {
	supplier: Supplier;
	enable: boolean;
}

const SuppliersTab = ({ canModify, canCreateAddress }: SuppliersTabProps) => {
	const [rows, setRows] = useState<Supplier[]>([]);
	const [groups, setGroups] = useState<SupplierGroup[]>([]);
	const [addressNamesById, setAddressNamesById] = useState<Record<number, string>>({});
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [referencesLoading, setReferencesLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState('');
	const [appliedSearch, setAppliedSearch] = useState('');
	const [groupFilter, setGroupFilter] = useState<SupplierGroup | null>(null);
	const [includeInactive, setIncludeInactive] = useState(false);
	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
	const [dialogSupplier, setDialogSupplier] = useState<Supplier | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [statusAction, setStatusAction] = useState<StatusAction | null>(null);
	const [statusLoading, setStatusLoading] = useState(false);

	const loadGroups = useCallback(() => {
		setReferencesLoading(true);
		supplierAdministrationApi.listSupplierGroups({ page: 1, limit: 100, includeInactive: true })
			.then((response) => setGroups(response.data))
			.catch((error: Error) => setError(error.message))
			.finally(() => setReferencesLoading(false));
	}, []);

	const loadSuppliers = useCallback(() => {
		setLoading(true);
		setError(null);
		supplierAdministrationApi.listSuppliers({
			page: paginationModel.page + 1,
			limit: paginationModel.pageSize,
			searchTerm: appliedSearch || undefined,
			groupId: groupFilter?.id,
			includeInactive,
		})
			.then((response) => {
				setRows(response.data);
				setTotal(response.total);
			})
			.catch((error: Error) => setError(error.message))
			.finally(() => setLoading(false));
	}, [appliedSearch, groupFilter?.id, includeInactive, paginationModel.page, paginationModel.pageSize]);

	useEffect(() => {
		loadGroups();
	}, [loadGroups]);

	useEffect(() => {
		loadSuppliers();
	}, [loadSuppliers]);

	useEffect(() => {
		const missingAddressIds = Array.from(
			new Set(
				rows
					.map((supplier) => supplier.addressId)
					.filter((addressId): addressId is number => !!addressId && !addressNamesById[addressId])
			)
		);

		if (missingAddressIds.length === 0) return;

		let isMounted = true;
		Promise.all(
			missingAddressIds.map((addressId) =>
				supplierAdministrationApi.getAddress(addressId)
					.then((address) => [addressId, address.name || `Adresse n° ${addressId}`] as const)
					.catch(() => [addressId, `Adresse n° ${addressId}`] as const)
			)
		).then((addresses) => {
			if (!isMounted) return;
			setAddressNamesById((current) => {
				const next = { ...current };
				addresses.forEach(([addressId, addressName]) => {
					next[addressId] = addressName;
				});
				return next;
			});
		});

		return () => {
			isMounted = false;
		};
	}, [addressNamesById, rows]);

	const submitSearch = () => {
		setPaginationModel((current) => ({ ...current, page: 0 }));
		setAppliedSearch(search.trim());
	};

	const openCreateDialog = () => {
		setDialogSupplier(null);
		setDialogOpen(true);
	};

	const openEditDialog = (supplier: Supplier) => {
		setDialogSupplier(supplier);
		setDialogOpen(true);
	};

	const submitSupplier = async (payload: SupplierPayload) => {
		if (dialogSupplier) {
			await supplierAdministrationApi.updateSupplier(dialogSupplier.id, payload);
			toast.success('Le fournisseur a bien été modifié.', { position: 'bottom-center', theme: 'colored' });
		} else {
			await supplierAdministrationApi.createSupplier(payload);
			toast.success('Le fournisseur a bien été créé.', { position: 'bottom-center', theme: 'colored' });
		}
		loadSuppliers();
		loadGroups();
	};

	const confirmStatusAction = async () => {
		if (!statusAction) return;

		setStatusLoading(true);
		try {
			if (statusAction.enable) {
				await supplierAdministrationApi.enableSupplier(statusAction.supplier.id);
				toast.success('Le fournisseur a bien été réactivé.', { position: 'bottom-center', theme: 'colored' });
			} else {
				await supplierAdministrationApi.disableSupplier(statusAction.supplier.id);
				toast.success('Le fournisseur a bien été désactivé.', { position: 'bottom-center', theme: 'colored' });
			}
			setStatusAction(null);
			loadSuppliers();
		} catch (error: any) {
			toast.error(error?.message || 'Une erreur est survenue.', { position: 'bottom-center', theme: 'colored' });
		} finally {
			setStatusLoading(false);
		}
	};

	const columns = useMemo<GridColDef<Supplier>[]>(
		() => [
			{ field: 'code', headerName: 'Code', flex: 0.4, minWidth: 110 },
			{ field: 'name', headerName: 'Nom', flex: 1, minWidth: 240 },
			{
				field: 'group',
				headerName: 'Groupe',
				flex: 0.8,
				minWidth: 180,
				valueGetter: (_, row) => `${row.group.code} - ${row.group.name}`,
			},
			{
				field: 'addressId',
				headerName: 'Adresse',
				flex: 0.7,
				minWidth: 180,
				valueGetter: (_, row) => row.addressId ? addressNamesById[row.addressId] || 'Chargement...' : 'Aucune adresse',
			},
			{
				field: 'isActive',
				headerName: 'Statut',
				flex: 0.4,
				minWidth: 120,
				valueGetter: (_, row) => row.isActive ? 'Actif' : 'Inactif',
			},
			{
				field: 'actions',
				type: 'actions',
				headerName: 'Actions',
				flex: 0.5,
				minWidth: 120,
				getActions: ({ row }) => [
					<GridActionsCellItem
						key="edit"
						icon={<EditIcon />}
						label="Modifier"
						disabled={!canModify}
						onClick={() => openEditDialog(row)}
					/>,
					row.isActive ? (
						<GridActionsCellItem
							key="disable"
							icon={<BlockIcon />}
							label="Désactiver"
							disabled={!canModify}
							onClick={() => setStatusAction({ supplier: row, enable: false })}
						/>
					) : (
						<GridActionsCellItem
							key="enable"
							icon={<RestartAltIcon />}
							label="Réactiver"
							disabled={!canModify}
							onClick={() => setStatusAction({ supplier: row, enable: true })}
						/>
					),
				],
			},
		],
		[addressNamesById, canModify]
	);

	return (
		<CustomPaper>
			<Grid container spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
				<Grid size={{ xs: 12, md: 3 }}>
					<Typography variant="h5" color="primary">Fournisseurs</Typography>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }}>
					<TextField
						label="Recherche"
						value={search}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'Enter') submitSearch();
						}}
						fullWidth
						size="small"
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 2 }}>
					<Autocomplete
						options={groups}
						value={groupFilter}
						onChange={(_, newValue) => {
							setGroupFilter(newValue);
							setPaginationModel((current) => ({ ...current, page: 0 }));
						}}
						getOptionLabel={(option) => `${option.code} - ${option.name}${option.isActive ? '' : ' (inactif)'}`}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						loading={referencesLoading}
						renderInput={(params) => <TextField {...params} label="Groupe" size="small" />}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 2 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={includeInactive}
								onChange={(event) => {
									setIncludeInactive(event.target.checked);
									setPaginationModel((current) => ({ ...current, page: 0 }));
								}}
							/>
						}
						label="Inclure les inactifs"
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 2 }}>
					<Box display="flex" gap={1} justifyContent="flex-end">
						<Button variant="contained" onClick={submitSearch}>Rechercher</Button>
						<Button variant="contained" color="success" disabled={!canModify} onClick={openCreateDialog}>Créer</Button>
					</Box>
				</Grid>
			</Grid>

			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
			<DataGrid
				rows={rows}
				columns={columns}
				loading={loading}
				rowCount={total}
				paginationMode="server"
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				pageSizeOptions={[10, 25, 50]}
				disableRowSelectionOnClick
				localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
				autoHeight
			/>

			<SupplierDialog
				open={dialogOpen}
				supplier={dialogSupplier}
				groups={groups}
				canCreateAddress={canCreateAddress}
				onClose={() => setDialogOpen(false)}
				onSubmit={submitSupplier}
			/>
			<ConfirmDialog
				open={!!statusAction}
				title={statusAction?.enable ? 'Réactiver le fournisseur' : 'Désactiver le fournisseur'}
				description={
					statusAction?.enable
						? `Réactiver le fournisseur ${statusAction.supplier.code} - ${statusAction.supplier.name} ? Cette action sera refusée si son groupe est désactivé.`
						: `Désactiver le fournisseur ${statusAction?.supplier.code} - ${statusAction?.supplier.name} ?`
				}
				confirmLabel={statusAction?.enable ? 'Réactiver' : 'Désactiver'}
				confirmColor={statusAction?.enable ? 'success' : 'error'}
				loading={statusLoading}
				onClose={() => setStatusAction(null)}
				onConfirm={confirmStatusAction}
			/>
		</CustomPaper>
	);
};

export default SuppliersTab;
